import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Modal } from '@mui/material';
import { Key, ArrowLeft, Loader, Plus, Trash2, Edit2, CheckCircle2, Shield, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, AlertTriangle, Calendar } from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { showError, showSuccess } from '../../../utils/toast';
import { jwt_decode } from '../../../utils/jwt_decode';
import CredentialForm from './CredentialForm';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';
import { useClientLevel } from '../../../hooks/useClientLevel';

export interface CredentialData {
    id?: string;
    credential_name: string;
    ip_addresses?: any
    credential_type: 'snmp' | 'firewall' | 'vmware' | 'camera';
    enabled: boolean;
    is_active?: boolean;

    // SNMP fields
    snmp_type?: 'v1' | 'v2c' | 'v3';
    community_string?: string;
    snmp_username?: string;
    auth_protocol?: string;
    privacy_protocol?: string;
    auth_password?: string;
    privacy_password?: string;

    // Firewall fields
    firewall_type?: 'cisco' | 'fortigate';
    hostname?: string;
    ssh_port?: number;
    https_port?: number;
    username?: string;
    password?: string;
    api_key?: string;

    // VMware fields
    vmware_type?: 'normal' | 'vcenter';
    vcenter_host?: string;
    port?: number;
    private_key?: string;
    make?: string;
    credential_data?: any;

    created?: string;
    updated?: string;
}

type ViewMode = 'list' | 'add' | 'edit';

const CredentialConfigPage: React.FC = () => {
    const { agentId } = useParams<{ agentId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [credentials, setCredentials] = useState<CredentialData[]>([]);
    const [selectedCredential, setSelectedCredential] = useState<CredentialData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [agent, setAgent] = useState<any>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [credentialToDelete, setCredentialToDelete] = useState<CredentialData | null>(null);
    const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
    const [credentialToToggle, setCredentialToToggle] = useState<CredentialData | null>(null);

    // Pagination states
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10); // Changed default to 10 for compact view
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const decodedToken = jwt_decode();
    const organizationId = decodedToken.organization_id;

    const queryParams = new URLSearchParams(location.search);
    const clientId = queryParams.get('client_id');
    const level = useClientLevel();
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (agentId) {
            fetchCredentials(0, itemsPerPage);
            setAgent({ id: agentId, client_id: clientId });
        }
    }, [agentId, clientId]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        navigate("/agents");
    }, [level]);

    const fetchCredentials = async (skip: number = 0, limit: number = 10) => {
        setIsLoading(true);
        const params = {
            query: JSON.stringify({
                agent_id: agentId,
                organization_id: organizationId
            }),
            skip: skip,
            limit: limit
        };

        try {
            const response = await baseapi.get('/credentials', { params });
            if (response.data.status) {
                setCredentials(response.data.data || []);
                setTotalPages(Math.ceil(response.data.total / limit));
                setTotalItems(response.data.total);
            }
        } catch (error) {
            console.error('Failed to fetch credentials:', error);
            showError('Failed to fetch credentials');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchCredentials((itemsPerPage * (page - 1)), itemsPerPage);
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setItemsPerPage(value);
        setCurrentPage(1);
        fetchCredentials(0, value);
    };

    const handleAddNew = () => {
        setSelectedCredential(null);
        setViewMode('add');
    };

    const handleEdit = (credential: CredentialData) => {
        setSelectedCredential(credential);
        setViewMode('edit');
    };

    const handleDelete = (credential: CredentialData) => {
        setCredentialToDelete(credential);
        setIsDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!credentialToDelete) return;
        try {
            const response = await baseapi.delete(`/credentials/${credentialToDelete.id}`);
            if (response.data.status) {
                showSuccess('Credential deleted successfully');
                await fetchCredentials((itemsPerPage * (currentPage - 1)), itemsPerPage);
            } else {
                showError(response.data.message);
            }
        } catch (error) {
            console.error('Failed to delete credential:', error);
            showError('Failed to delete credential');
        } finally {
            setIsDeleteDialogOpen(false);
            setCredentialToDelete(null);
        }
    };

    const handleFormSuccess = async () => {
        await fetchCredentials();
        setViewMode('list');
        setSelectedCredential(null);
    };

    const handleStatusToggle = (credential: CredentialData) => {
        setCredentialToToggle(credential);
        setIsStatusDialogOpen(true);
    };

    const confirmStatusToggle = async () => {
        if (!credentialToToggle) return;
        try {
            const newState = !credentialToToggle.is_active;
            const response = await baseapi.put(`/credentials/${credentialToToggle.id}`, { is_active: newState });
            if (response.data.status) {
                showSuccess(`Credential ${newState ? 'enabled' : 'disabled'} successfully`);
                setCredentials(prev => prev.map(c => c.id === credentialToToggle.id ? { ...c, is_active: newState } : c));
            } else {
                showError(response.data.message);
            }
        } catch (error) {
            console.error('Failed to update status:', error);
            showError('Failed to update status');
        } finally {
            setIsStatusDialogOpen(false);
            setCredentialToToggle(null);
        }
    };

    const getCredentialTypeBadge = (credential: CredentialData) => {
        let label = credential.credential_type.toUpperCase();
        let colorClass = 'bg-gray-800 text-gray-400 border-gray-700';

        let parsedData: any = {};
        if (credential.credential_data) {
            try {
                parsedData = typeof credential.credential_data === 'string'
                    ? JSON.parse(credential.credential_data)
                    : credential.credential_data;
            } catch (e) {
                console.error('Failed to parse credential_data for badge:', e);
            }
        }

        if (credential.credential_type === 'snmp') {
            const snmpVersion = parsedData.snmp_version || credential.snmp_type;
            label = `SNMP ${snmpVersion?.toUpperCase() || ''}`;
            colorClass = 'bg-blue-500/10 text-blue-500 border-blue-500/20';
        } else if (credential.credential_type === 'firewall') {
            const firewallType = parsedData.firewall_type || credential.firewall_type;
            label = `${firewallType?.toUpperCase() || 'FIREWALL'}`;
            colorClass = 'bg-red-500/10 text-red-500 border-red-500/20';
        } else if (credential.credential_type === 'vmware') {
            const vmwareType = parsedData.vmware_type || credential.vmware_type;
            label = `VMware ${vmwareType?.toUpperCase() || ''}`;
            colorClass = 'bg-green-500/10 text-green-500 border-green-500/20';
        } else if (credential.credential_type === 'camera') {
            label = 'Camera';
            colorClass = 'bg-purple-500/10 text-purple-500 border-purple-500/20';
        }

        return (
            <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border ${colorClass}`}>
                {label}
            </span>
        );
    };

    const renderCompactTable = () => (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                        <tr>
                            <th className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">
                                <div className="flex items-center gap-1.5">
                                    <Key size={12} className="text-[#422568]" />
                                    Name
                                </div>
                            </th>
                            <th className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">
                                <div className="flex items-center gap-1.5">
                                    <Shield size={12} className="text-[#422568]" />
                                    Type
                                </div>
                            </th>
                            <th className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">
                                <div className="flex items-center gap-1.5">
                                    <CheckCircle2 size={12} className="text-[#422568]" />
                                    Status
                                </div>
                            </th>
                            <th className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">
                                <div className="flex items-center gap-1.5">
                                    <Calendar size={12} className="text-[#422568]" />
                                    Created
                                </div>
                            </th>
                            <th className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">
                                <div className="flex items-center gap-1.5">
                                    <Calendar size={12} className="text-[#422568]" />
                                    Updated
                                </div>
                            </th>
                            <th className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right leading-none">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {credentials.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="py-12 text-center text-slate-400 font-bold uppercase tracking-widest text-xs italic">
                                    No credentials configured
                                </td>
                            </tr>
                        ) : (
                            credentials.map((cred) => (
                                <tr key={cred.id} className="hover:bg-slate-50 transition-colors group">
                                    <td className="px-3 py-1.5">
                                        <div className="flex flex-col">
                                            <span className="text-slate-800 text-xs font-bold">{cred.credential_name}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-1.5">
                                        {getCredentialTypeBadge(cred)}
                                    </td>
                                    <td className="px-3 py-1.5">
                                        <button
                                            onClick={() => handleStatusToggle(cred)}
                                            className="flex items-center gap-1.5 group transition-all"
                                            title={cred.is_active ? 'Disable Credential' : 'Enable Credential'}
                                        >
                                            <div className={`
                                                relative w-6 h-3 rounded-full transition-all duration-300 ease-in-out border
                                                ${cred.is_active ? 'bg-[#422568] border-[#422568]' : 'bg-slate-200 border-slate-200'}
                                            `}>
                                                <div className={`
                                                    absolute top-[1px] w-2 h-2 rounded-full bg-white transition-all duration-300 ease-in-out shadow-sm
                                                    ${cred.is_active ? 'left-[13px]' : 'left-[1px]'}
                                                `} />
                                            </div>
                                            <span className={`text-[8px] font-black uppercase tracking-wider ${cred.is_active ? 'text-[#422568]' : 'text-slate-400'}`}>
                                                {cred.is_active ? 'Active' : 'Disabled'}
                                            </span>
                                        </button>
                                    </td>
                                    <td className="px-3 py-1.5">
                                        <span className="text-slate-500 text-[10px] font-medium">
                                            {cred.created ? new Date(cred.created.slice(0, 23) + "Z").toLocaleString() : "-"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-1.5">
                                        <span className="text-slate-500 text-[10px] font-medium">
                                            {cred.updated ? new Date(cred.updated.slice(0, 23) + "Z").toLocaleString() : "-"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                onClick={() => handleEdit(cred)}
                                                className="p-1 px-1.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded transition-all font-black text-[9px] uppercase tracking-wider flex items-center gap-1"
                                                title="Edit"
                                            >
                                                <Edit2 className="w-3 h-3" />
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(cred)}
                                                className="p-1 px-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-all font-black text-[9px] uppercase tracking-wider flex items-center gap-1"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                                Del
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Footer */}
            <div className="bg-slate-50 border-t border-slate-200 px-3 py-1.5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <ItemsPerPageDropdown
                        value={itemsPerPage}
                        onChange={(val: any) => handleItemsPerPageChange(val)}
                    />
                    <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest leading-none">
                        Showing <span className="text-slate-800">{startIndex + 1}</span> - <span className="text-slate-800">{Math.min(endIndex, totalItems)}</span> of {totalItems}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className="p-1 text-slate-400 hover:text-slate-800 hover:bg-white rounded disabled:opacity-30 transition-all border border-transparent hover:border-slate-200"
                    >
                        <ChevronsLeft className="w-3 h-3" />
                    </button>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-1 text-slate-400 hover:text-slate-800 hover:bg-white rounded disabled:opacity-30 transition-all border border-transparent hover:border-slate-200"
                    >
                        <ChevronLeft className="w-3 h-3" />
                    </button>
                    <div className="min-w-[20px] h-[20px] flex items-center justify-center bg-[#422568] rounded text-white text-[10px] font-bold shadow-sm mx-0.5">
                        {currentPage}
                    </div>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="p-1 text-slate-400 hover:text-slate-800 hover:bg-white rounded disabled:opacity-30 transition-all border border-transparent hover:border-slate-200"
                    >
                        <ChevronRight className="w-3 h-3" />
                    </button>
                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="p-1 text-slate-400 hover:text-slate-800 hover:bg-white rounded disabled:opacity-30 transition-all border border-transparent hover:border-slate-200"
                    >
                        <ChevronsRight className="w-3 h-3" />
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="bg-slate-50 min-h-screen p-3">
            <div className="mx-auto">
                {/* Header Row */}
                <div className="flex flex-row items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/agents')}
                            className="p-1.5 bg-white border border-slate-200 rounded text-slate-400 hover:text-slate-800 hover:bg-slate-50 shadow-sm"
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                        <div className="flex items-center gap-2">
                            <div className="bg-[#422568] p-1.5 rounded shadow-sm">
                                <Key className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h1 className="font-extrabold text-base text-slate-800 tracking-tight leading-none mb-0.5">Credential Config</h1>
                                <div className="flex items-center gap-1.5">
                                    <span className="px-1 py-0.5 bg-white text-slate-400 text-[8px] font-bold uppercase tracking-widest rounded border border-slate-200 leading-none">Agent ID</span>
                                    <span className="text-slate-500 font-mono text-[9px] font-medium leading-none">{agentId}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {viewMode === 'list' && (
                        <button
                            onClick={handleAddNew}
                            className="flex items-center justify-center gap-1.5 bg-[#422568] hover:bg-[#422568] text-white px-3 py-1.5 rounded text-[11px] font-bold shadow-sm transition-all uppercase tracking-wider"
                        >
                            <Plus className="w-3 h-3" />
                            Add
                        </button>
                    )}
                </div>

                {/* Main Content Area */}
                <div className="relative">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-200 rounded-lg shadow-sm">
                            <Loader className="w-8 h-8 text-[#422568] animate-spin mb-3" />
                            <span className="text-slate-400 text-[10px] font-bold uppercase tracking-widest animate-pulse">Syncing config...</span>
                        </div>
                    ) : (
                        <>
                            {viewMode === 'list' ? (
                                renderCompactTable()
                            ) : (
                                <div className="max-w-2xl mx-auto py-2 animate-in fade-in duration-300">
                                    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                                        <CredentialForm
                                            agent={agent}
                                            organizationId={organizationId}
                                            credential={selectedCredential}
                                            isEdit={viewMode === 'edit'}
                                            onSuccess={handleFormSuccess}
                                            onBack={() => setViewMode('list')}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Compact Delete Confirmation Modal */}
            <Modal open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                            w-[90%] sm:w-[320px] bg-white border border-slate-200 shadow-xl p-5 rounded-xl text-slate-800 outline-none">
                    <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3 border-2 border-white shadow-sm">
                            <Trash2 className="w-6 h-6 text-red-500" />
                        </div>
                        <h2 className="text-lg font-black mb-1.5">Delete Credential</h2>
                        <p className="text-slate-500 text-xs mb-5">
                            Delete <span className="text-slate-800 font-bold">"{credentialToDelete?.credential_name}"</span>?<br />
                            This cannot be undone.
                        </p>
                        <div className="flex w-full gap-2">
                            <button
                                onClick={() => setIsDeleteDialogOpen(false)}
                                className="flex-1 px-2 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded text-[10px] font-bold uppercase tracking-wider border border-slate-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="flex-1 px-2 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-[10px] font-bold uppercase tracking-wider shadow-sm"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Compact Status Toggle Confirmation Modal */}
            <Modal open={isStatusDialogOpen} onClose={() => setIsStatusDialogOpen(false)}>
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                            w-[90%] sm:w-[320px] bg-white border border-slate-200 shadow-xl p-5 rounded-xl text-slate-800 outline-none">
                    <div className="flex flex-col items-center text-center">
                        <div className={`w-12 h-12 ${credentialToToggle?.is_active ? 'bg-[#422568]' : 'bg-green-50'} rounded-full flex items-center justify-center mb-3 border-2 border-white shadow-sm`}>
                            <AlertTriangle className={`w-6 h-6 ${credentialToToggle?.is_active ? 'text-[#422568]' : 'text-green-500'}`} />
                        </div>
                        <h2 className="text-lg font-black mb-1.5">
                            {credentialToToggle?.is_active ? 'Disable Credential' : 'Enable Credential'}
                        </h2>
                        <p className="text-slate-500 text-xs mb-5">
                            {credentialToToggle?.is_active ? 'Disable' : 'Enable'} <span className="text-slate-800 font-bold">"{credentialToToggle?.credential_name}"</span>?
                        </p>
                        <div className="flex w-full gap-2">
                            <button
                                onClick={() => setIsStatusDialogOpen(false)}
                                className="flex-1 px-2 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded text-[10px] font-bold uppercase tracking-wider border border-slate-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmStatusToggle}
                                className={`flex-1 px-2 py-2 ${credentialToToggle?.is_active ? 'bg-[#422568] hover:bg-[#422568]' : 'bg-green-600 hover:bg-green-700'} text-white rounded text-[10px] font-bold uppercase tracking-wider shadow-sm`}
                            >
                                {credentialToToggle?.is_active ? 'Disable' : 'Enable'}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CredentialConfigPage;